<template>
  <component
    :is="quotationComponent"
    v-bind="{ ...$props }"
    :title-text-data="titleTextData"
    :order-detail-type="orderDetailType"
    :basic-id="basicId"
    :premium-id="premiumId"
    :display-name="displayName"
    :full-name="fullName"
    :company-text="companyText"
    :address-text="addressText"
    :branch-number-text="branchNumberText"
    :valid-at="validAt"
    :package-started-at="packageStartedAt"
    :package-expired-at="packageExpiredAt"
    :premium-id-started-at="premiumIdStartedAt"
    :premium-id-expired-at="premiumIdExpiredAt"
    :grand-total-text="grandTotalText"
    :is-juristic-customer="isJuristicCustomer"
    :is-deduct-withholding="isDeductWithholding"
  />
</template>

<script>
import thaiBahtText from '../libs/thaiBahtText'
import { roundDecimalDigits } from '../utils/number'
import { isMobile } from '../utils/deviceDetect'
import { isBkkZipCode } from '../utils/zipCode'

export default {
  name: 'LineDiyQuotation',
  components: {
    DesktopLineDiyQuotation: () => import('./DesktopLineDiyQuotation'),
    MobileLineDiyQuotation: () => import('./MobileLineDiyQuotation')
  },
  props: {
    order: {
      type: Object,
      default: () => ({})
    },
    orderDetailType: {
      type: String,
      default: ''
    },
    basicId: {
      type: String,
      default: ''
    }
  },
  computed: {
    premiumId() {
      const orderPremiumId = this.order?.orderItems?.find((item) => item.productType === 'premiumId')

      if (orderPremiumId) {
        return orderPremiumId?.premiumId
      }

      return ''
    },
    quotationComponent () {
      if (isMobile()) {
        return () => import('./MobileLineDiyQuotation')
      }

      return () => import('./DesktopLineDiyQuotation')
    },
    titleTextData () {
      const titleData = {
        quotation: {
          th: 'ใบเสนอราคา',
          en: 'Quotation'
        },
        invoice: {
          th: 'ใบแจ้งหนี้',
          en: 'Invoice'
        }
      }

      return titleData[this.orderDetailType] || {}
    },
    fullName () {
      const { firstName = '', lastName = '' } = this.order
      if (!firstName && !lastName) {
        return '-'
      }

      return `${firstName} ${lastName}`
    },
    companyText () {
      const { companyName, customerTypeId } = this.order

      switch (customerTypeId) {
        case '1':
          return `ห้างหุ้นส่วนจำกัด ${companyName}`
        case '2':
          return `บริษัท ${companyName} จำกัด`
        case '3':
          return `บริษัท ${companyName} จำกัด (มหาชน)`
        case '4':
          return `ร้านค้า ${companyName}`
        case '6':
          return `คณะบุคคล ${companyName}`
        case '7':
          return `ห้างหุ้นส่วนสามัญ ${companyName}`
        case '8':
          return `มูลนิธิ ${companyName}`
        case '9':
          return `สมาคม ${companyName}`
        case '10':
          return `กิจการร่วมค้า ${companyName}`
        default:
          return companyName
      }
    },
    addressText () {
      const { zipCode, address, street, subdistrict, district, stateProvince } = this.order

      const addressPrefix = street ? `${address} ถนน${street}` : address

      if (isBkkZipCode(zipCode)) {
        return `${addressPrefix} แขวง${subdistrict} เขต${district} กรุงเทพมหานคร ${zipCode}`
      }

      return `${addressPrefix} ต.${subdistrict} อ.${district} จ.${stateProvince} ${zipCode}`
    },
    branchNumberText () {
      if (this.order.branchNumber === '00000') {
        return '(สำนักงานใหญ่)'
      }

      return `(สาขาที่ ${this.order.branchNumber})`
    },
    validAt () {
      if (!this.order.createdAt) {
        return undefined
      }

      return this.$utils.createDayjsDate(this.order.createdAt).add(30, 'day').toDate()
    },
    packageStartedAt () {
      if (!this.order.orderItems) {
        return undefined
      }

      const findPackageProduct = this.order.orderItems.find((item) => item.productType === 'package')

      return findPackageProduct ? findPackageProduct.startedAt : undefined
    },
    packageExpiredAt () {
      if (!this.order.orderItems) {
        return undefined
      }

      const findPackageProduct = this.order.orderItems.find((item) => item.productType === 'package')

      return findPackageProduct ? findPackageProduct.expiredAt : undefined
    },
    premiumIdStartedAt () {
      if (!this.order.orderItems) {
        return undefined
      }

      const findPremiumIdProduct = this.order.orderItems.find((item) => item.productType === 'premiumId')

      return findPremiumIdProduct ? findPremiumIdProduct.startedAt : undefined
    },
    premiumIdExpiredAt () {
      if (!this.order.orderItems) {
        return undefined
      }

      const findPremiumIdProduct = this.order.orderItems.find((item) => item.productType === 'premiumId')

      return findPremiumIdProduct ? findPremiumIdProduct.expiredAt : undefined
    },
    grandTotalText () {
      return this.order.grandTotal ? thaiBahtText(roundDecimalDigits(this.order.grandTotal)) : '-'
    },
    isJuristicCustomer () {
      if (!this.order.customerTypeId) {
        return false
      }

      return this.order.customerTypeId !== '5'
    },
    isDeductWithholding () {
      return this.isJuristicCustomer && this.order.preVatAmount >= 1000
    }
  }
}
</script>
